import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowLeft, Sparkles, Check } from "lucide-react";
import { aiAPI } from "../../api";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

const TASK_DESC = {
  task1: { label: "Task 1", desc: "Describe a chart, graph, or process. 150 words.", minWords: 130 },
  task2: { label: "Task 2", desc: "Opinion or argumentative essay. 250 words.",     minWords: 230 },
};

function bandColor(b) {
  if (b >= 7)   return "var(--sage)";
  if (b >= 6)   return "var(--gold-500)";
  if (b >= 5)   return "var(--rust)";
  return "var(--berry)";
}

export default function IeltsWriting() {
  const [task, setTask] = useState("task2");
  const [text, setText] = useState("");
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  async function evaluate() {
    if (!text.trim()) { setError("Paste your essay first."); return; }
    if (wordCount < 80) { setError(`Too short. Aim for at least ${TASK_DESC[task].minWords} words.`); return; }
    setError(null);
    setLoading(true);
    setEvaluation(null);
    try {
      const data = await aiAPI.ieltsEvaluate(task, text);
      setEvaluation(data.evaluation);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/write" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Write
        </Link>
        <Chip tone="ink">IELTS Writing</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          Your essay. <em style={{ color: "var(--gold-500)" }}>An examiner's read.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Paste your Task 1 or Task 2 response. Get a band score across the four IELTS criteria, plus the three sentences worth fixing first.
        </p>
      </div>

      {/* Task picker */}
      <div style={{ display: "flex", gap: 8 }}>
        {Object.entries(TASK_DESC).map(([id, t]) => (
          <button key={id} type="button" onClick={() => setTask(id)} style={{
            flex: 1, padding: "14px 16px", textAlign: "left",
            background: task === id ? "var(--ink-900)" : "#fff",
            color: task === id ? "var(--paper)" : "var(--fg-1)",
            border: `1px solid ${task === id ? "var(--ink-900)" : "var(--border-2)"}`,
            borderRadius: "var(--radius-md)", cursor: "pointer",
            transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
          }}>
            <p style={{ fontWeight: 700, fontSize: 14, margin: 0 }}>{t.label}</p>
            <p style={{ fontSize: 12, opacity: 0.7, margin: "2px 0 0" }}>{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Input */}
      <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your essay here…"
          rows={14}
          style={{
            width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, lineHeight: 1.7,
            border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
            padding: "14px 16px", resize: "vertical", background: "#fff", color: "var(--fg-1)", outline: "none",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: wordCount < TASK_DESC[task].minWords ? "var(--rust)" : "var(--sage-dark)", fontWeight: 600 }}>
            {wordCount} word{wordCount === 1 ? "" : "s"} · target {TASK_DESC[task].minWords}+
          </span>
          <Button variant="primary" onClick={evaluate} disabled={loading || !text.trim()} iconLeft={<Sparkles size={15} strokeWidth={1.8} />}>
            {loading ? "Evaluating…" : "Get band score"}
          </Button>
        </div>
        {error && <p style={{ color: "var(--berry)", fontSize: 13, margin: 0 }}>{error}</p>}
      </div>

      {/* Result */}
      {evaluation && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Band scores */}
          <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: `3px solid ${bandColor(evaluation.overall)}`, borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
              <p className="eyebrow">Overall band</p>
              <p className="font-display" style={{ fontSize: 64, lineHeight: 1, fontWeight: 400, margin: 0, color: bandColor(evaluation.overall) }}>
                {evaluation.overall?.toFixed(1)}
              </p>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              {[
                ["Task response", evaluation.taskResponse],
                ["Coherence",     evaluation.coherenceCohesion],
                ["Vocabulary",    evaluation.lexicalResource],
                ["Grammar",       evaluation.grammar],
              ].map(([label, b]) => (
                <div key={label} style={{ background: "var(--paper-deep)", borderRadius: "var(--radius-md)", padding: "12px 14px", textAlign: "center" }}>
                  <p className="eyebrow" style={{ fontSize: 9, margin: "0 0 6px" }}>{label}</p>
                  <p className="font-display" style={{ fontSize: 28, lineHeight: 1, fontWeight: 400, margin: 0, color: bandColor(b) }}>{b?.toFixed(1)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths */}
          {evaluation.strengths?.length > 0 && (
            <article style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: "3px solid var(--sage)", borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
              <p className="eyebrow">What's working</p>
              <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
                {evaluation.strengths.map((s, i) => (
                  <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderBottom: i < evaluation.strengths.length - 1 ? "1px solid var(--border-1)" : "none" }}>
                    <Check size={16} strokeWidth={1.8} color="var(--sage-dark)" style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 14.5, lineHeight: 1.55, color: "var(--fg-1)" }}>{s}</span>
                  </li>
                ))}
              </ul>
            </article>
          )}

          {/* Improvements */}
          {evaluation.improvements?.length > 0 && (
            <article style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: "3px solid var(--rust)", borderRadius: "var(--radius-lg)", padding: "20px 24px" }}>
              <p className="eyebrow">Three to fix first</p>
              <ol style={{ paddingLeft: 22, margin: "10px 0 0" }}>
                {evaluation.improvements.map((s, i) => (
                  <li key={i} style={{ fontSize: 14.5, lineHeight: 1.6, color: "var(--fg-1)", marginBottom: 10 }}>{s}</li>
                ))}
              </ol>
            </article>
          )}
        </div>
      )}
    </div>
  );
}
